import {gql} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {Box, Group, Paper, Stack, Text, createStyles} from '@mantine/core';
import React from 'react';

import {AntibodySiteMarkerIcon} from 'antibodies/visualization/AntibodySiteMarker';
import {pluralize} from 'BenchlingUtil';
import ChemicalStructureSVG, {ChemicalStructureImageData} from 'chemistry/ChemicalStructureSVG';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  AdcLinkerPayloadStructuresQuery,
  AdcLinkerPayloadStructuresQueryVariables,
} from 'molbio/adcs/adc-map/__generated/AdcLinkerPayloadStructuresQuery';
import {AdcMapQuery} from 'molbio/adcs/adc-map/__generated/AdcMapQuery';
import {CONJUGATION_METHOD_TO_DISPLAY_NAME} from 'molbio/adcs/AdcConjugationConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {CenteredSpinner} from 'ui-library/Spinner';

const SITE_MARKER_ICON_RADIUS = 8;

// Structures are drawn by the chemistry service, so they are queried apart from the ADC so as not to hold up
// drawing the antibody.
export const ADC_LINKER_PAYLOAD_STRUCTURES_QUERY = gql`
  query AdcLinkerPayloadStructuresQuery($moleculeIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: MOLBIO) {
    molecules(ids: $moleculeIds) {
      ... on MoleculeNode {
        id
        imageData {
          ... on SVGNode {
            base64Svg
          }
          ... on ErrorNode {
            message
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export type AdcConjugation = ExtractByTypename<
  AdcMapQuery['antibodyDrugConjugate'],
  'AntibodyDrugConjugateNode'
>['conjugations'][number];

/**
 * The structure shown on a card: `PENDING` while it loads, or null when it can't be shown because the linker
 * payload is not viewable or its structure failed to load.
 */
type LinkerPayloadStructure = ChemicalStructureImageData | QueryResultType.PENDING | null;

const useCardStyles = createStyles(() => ({
  card: {
    width: '40%',
    flexShrink: 0,
  },
  structure: {
    maxHeight: '60px',
  },
  siteSummary: {
    color: Color.GRAY_500,
  },
}));

interface LinkerPayloadCardProps {
  conjugation: AdcConjugation;
  structure: LinkerPayloadStructure;
}

function LinkerPayloadCard({conjugation, structure}: LinkerPayloadCardProps): JSX.Element {
  const {classes} = useCardStyles();
  const siteCount = conjugation.conjugationSites.length;

  return (
    <Paper
      withBorder={true}
      p="xs"
      className={classes.card}
      {...getTestProps({component: 'LinkerPayloadCard', element: 'root'})}
    >
      <Stack spacing="xs">
        <Group spacing="xs">
          {/* Matches the markers of the conjugation's sites on the map. */}
          <svg
            aria-hidden={true}
            width={SITE_MARKER_ICON_RADIUS * 2}
            height={SITE_MARKER_ICON_RADIUS * 2}
            viewBox={`${-SITE_MARKER_ICON_RADIUS} ${-SITE_MARKER_ICON_RADIUS} ${SITE_MARKER_ICON_RADIUS * 2} ${SITE_MARKER_ICON_RADIUS * 2}`}
          >
            <AntibodySiteMarkerIcon radius={SITE_MARKER_ICON_RADIUS} count={1} />
          </svg>
          <BioentityChip size={ChipSize.MINI} id={getAccessibleOrInaccessibleId(conjugation.linkerPayload)} />
        </Group>
        {structure === QueryResultType.PENDING ? (
          <CenteredSpinner aria-label="Loading structure" />
        ) : (
          structure != null && (
            <ChemicalStructureSVG chemicalStructureImage={structure} svgClassName={classes.structure} />
          )
        )}
        <Text size="sm" className={classes.siteSummary}>
          {`${siteCount} ${pluralize(siteCount, 'site')} (${CONJUGATION_METHOD_TO_DISPLAY_NAME[conjugation.conjugationMethod]})`}
        </Text>
      </Stack>
    </Paper>
  );
}

const useStripStyles = createStyles((theme) => ({
  strip: {
    backgroundColor: Color.GRAY_100,
    marginTop: theme.spacing.sm,
  },
  scroller: {
    overflowX: 'auto',
    overflowY: 'hidden',
  },
}));

export interface LinkerPayloadCardsProps {
  conjugations: ReadonlyArray<AdcConjugation>;
}

/**
 * A card for each conjugation of an ADC, showing its linker payload, the payload's structure, and the number
 * of sites it is conjugated at, in a row that scrolls sideways when the cards overflow it.
 */
export function LinkerPayloadCards({conjugations}: LinkerPayloadCardsProps): JSX.Element {
  const {classes} = useStripStyles();
  const moleculeIds = [
    ...new Set(
      conjugations.flatMap(({linkerPayload}) => (isInaccessible(linkerPayload) ? [] : [linkerPayload.id]))
    ),
  ];
  const result = useSkippableBenchlingQuery<
    AdcLinkerPayloadStructuresQuery,
    AdcLinkerPayloadStructuresQueryVariables
  >(ADC_LINKER_PAYLOAD_STRUCTURES_QUERY, {variables: {moleculeIds}, skip: moleculeIds.length === 0});

  const structuresById = new Map<string, ChemicalStructureImageData>();
  if (result.type === QueryResultType.SUCCESS) {
    for (const molecule of result.data.molecules) {
      if (!isInaccessible(molecule)) {
        structuresById.set(molecule.id, molecule.imageData);
      }
    }
  }
  const getStructure = ({linkerPayload}: AdcConjugation): LinkerPayloadStructure => {
    if (isInaccessible(linkerPayload)) {
      return null;
    }
    if (result.type === QueryResultType.PENDING) {
      return QueryResultType.PENDING;
    }
    return structuresById.get(linkerPayload.id) ?? null;
  };

  return (
    <Paper
      p="xs"
      className={classes.strip}
      {...getTestProps({component: 'LinkerPayloadCards', element: 'root'})}
    >
      <Box className={classes.scroller}>
        <Group noWrap={true}>
          {conjugations.map((conjugation) => (
            <LinkerPayloadCard
              key={conjugation.id}
              conjugation={conjugation}
              structure={getStructure(conjugation)}
            />
          ))}
        </Group>
      </Box>
    </Paper>
  );
}
